import { useEffect, useState } from "react";
import { api, gbp, airlineLogo } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, Search, CheckCircle2, Clock, Plane, Ticket, CreditCard,
  FileText, Download, Users, ChevronRight, ShieldCheck, ArrowRight,
} from "lucide-react";

const Leg = ({ l }) => (
  <div className="flex items-center gap-4 py-3">
    <div className="w-9 h-9 rounded-lg bg-slate-50 ring-1 ring-slate-100 flex items-center justify-center shrink-0">
      <img src={airlineLogo(l.airline_code)} alt={l.airline} className="h-5 w-auto object-contain" onError={(e) => { e.target.style.display = "none"; }} />
    </div>
    <div className="w-28 shrink-0">
      <p className="text-[11px] text-slate-400 leading-none">{l.flight_no}</p>
      <p className="text-xs text-slate-500 mt-1">{l.date}</p>
    </div>
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="text-center">
        <p className="font-display text-lg font-bold text-slate-900 leading-none">{l.from_code}</p>
        <p className="text-[13px] text-slate-700">{l.dep}</p>
      </div>
      <div className="flex-1 flex flex-col items-center px-1">
        <p className="text-[10px] text-slate-400">{l.duration}</p>
        <div className="w-full flex items-center gap-1 text-slate-300"><span className="flex-1 h-px bg-slate-200" /><Plane className="w-3 h-3" /><span className="flex-1 h-px bg-slate-200" /></div>
        <p className="text-[10px] text-slate-400">{l.stops}</p>
      </div>
      <div className="text-center">
        <p className="font-display text-lg font-bold text-slate-900 leading-none">{l.to_code}</p>
        <p className="text-[13px] text-slate-700">{l.arr}</p>
      </div>
    </div>
    <div className="w-40 shrink-0 text-right hidden md:block">
      <p className="text-xs text-slate-500">{l.from} → {l.to}</p>
      {l.note && <p className="text-[10.5px] text-slate-400 mt-0.5">{l.note}</p>}
    </div>
  </div>
);

const Itinerary = ({ it, idx }) => {
  const outbound = (it.legs || []).filter((l) => l.label === "Outbound");
  const inbound = (it.legs || []).filter((l) => l.label === "Return");
  return (
    <div data-testid={`mb-itinerary-${idx}`} className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-display text-base md:text-lg font-semibold text-slate-900 flex items-center gap-2"><Plane className="w-4 h-4 text-[#FF6B35]" /> {it.name}</p>
        <span className="font-display text-lg font-bold text-[#0B3C5D]">{gbp(it.price)}</span>
      </div>
      {/* Passengers on this booking */}
      <div className="flex flex-wrap gap-2 mt-3">
        {(it.passengers || []).map((p, i) => (
          <span key={i} data-testid={`mb-itin-${idx}-pax-${i}`} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1 text-[12.5px] text-slate-700">
            <Users className="w-3 h-3 text-[#328CC1]" /> {p}
          </span>
        ))}
      </div>
      {it.note && <p className="text-[12.5px] text-slate-500 mt-3 flex items-start gap-1.5"><ChevronRight className="w-3.5 h-3.5 mt-0.5 text-[#FF6B35] shrink-0" /> {it.note}</p>}

      <div className="mt-3">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><ArrowRight className="w-3.5 h-3.5 text-[#FF6B35]" /> Outbound</p>
        <div className="divide-y divide-slate-100 mt-1">{outbound.map((l, i) => <Leg key={i} l={l} />)}</div>
      </div>
      {inbound.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><ArrowRight className="w-3.5 h-3.5 text-[#FF6B35] rotate-180" /> Return</p>
          <div className="divide-y divide-slate-100 mt-1">{inbound.map((l, i) => <Leg key={i} l={l} />)}</div>
        </div>
      )}
      {it.eticket_url && (
        <a href={it.eticket_url} target="_blank" rel="noopener noreferrer" data-testid={`mb-itin-${idx}-eticket`} className="inline-block mt-4">
          <Button variant="outline" size="sm" className="rounded-full"><Ticket className="w-4 h-4 mr-2 text-[#FF6B35]" /> Download booking confirmation</Button>
        </a>
      )}
    </div>
  );
};

export default function ManageBooking() {
  const [reference, setReference] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [terms, setTerms] = useState([]);

  useEffect(() => { api.get("/legal/terms").then(({ data }) => setTerms(data.terms || [])).catch(() => {}); }, []);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setBooking(null);
    try {
      const { data } = await api.get("/bookings/lookup/find", { params: { reference, ...(name ? { name } : {}) } });
      setBooking(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No booking found.");
    } finally { setLoading(false); }
  };

  const paid = booking && (booking.payment_status === "paid" || booking.status === "confirmed");
  const eticketPending = booking && booking.e_ticket_status !== "Issued";
  // Support both merged (itineraries[]) and legacy single-trip (legs[]) bookings.
  const itineraries = booking?.itineraries?.length
    ? booking.itineraries
    : booking?.legs?.length
      ? [{ name: booking.item_title, passengers: booking.passengers, price: booking.amount, legs: booking.legs, eticket_url: booking.eticket_url }]
      : [];

  return (
    <div>
      <PageHero title="Manage my booking" subtitle="Enter your booking reference to view your confirmed trip, e-tickets and invoice." image="https://images.unsplash.com/photo-1436491865332-7a61a109cc05" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <form onSubmit={lookup} className="bg-white rounded-2xl border border-slate-200 p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div><Label>Booking reference</Label><Input data-testid="mb-reference" required value={reference} onChange={(e) => setReference(e.target.value.toUpperCase())} placeholder="Enter your booking reference" /></div>
          <div><Label>Last name</Label><Input data-testid="mb-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your last name" /></div>
          <Button data-testid="mb-search" disabled={loading} className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold h-10">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Find</>}
          </Button>
        </form>

        {error && <p data-testid="mb-error" className="text-center text-slate-500 mt-6">{error}</p>}

        {booking && (
          <div data-testid="mb-result" className="mt-6 space-y-5">
            {/* Header */}
            <div className="bg-[#0B3C5D] text-white rounded-2xl overflow-hidden">
              <div className="p-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-slate-300 uppercase tracking-widest">Booking reference (PNR)</p>
                  <p className="font-display text-2xl font-bold tracking-widest">{booking.reference}</p>
                  <p className="text-[12px] text-slate-300 mt-1">{booking.item_title}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1 ${paid ? "bg-[#16a34a] text-white" : "bg-amber-400 text-slate-900"}`}>
                    {paid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />} {paid ? "Confirmed" : "Pending payment"}
                  </span>
                  {booking.e_ticket_issued && (
                    <span data-testid="mb-eticket-badge" className={`text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1 ${eticketPending ? "bg-amber-400 text-slate-900" : "bg-white/15 text-white"}`}>
                      {eticketPending ? <Clock className="w-3.5 h-3.5" /> : <Ticket className="w-3.5 h-3.5" />} {eticketPending ? `E-ticket: to be issued ${booking.e_ticket_issued}` : "E-ticket issued"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Promo / e-ticket notice */}
            {booking.promo_note && (
              <div data-testid="mb-promo-note" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
                <Ticket className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">E-tickets to be issued on {booking.e_ticket_issued} · Promotional fare</p>
                  <p className="text-[13px] text-amber-800 mt-1">{booking.promo_note}</p>
                </div>
              </div>
            )}

            {/* All travellers summary */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3"><Users className="w-3.5 h-3.5 text-[#328CC1]" /> All travellers ({(booking.passengers || []).length})</p>
              <div className="flex flex-wrap gap-2">
                {(booking.passengers || [booking.lead_name]).map((p, i) => (
                  <span key={i} data-testid={`mb-pax-${i}`} className="inline-flex items-center gap-1.5 text-sm text-slate-800"><CheckCircle2 className="w-4 h-4 text-[#16a34a]" /> {p}</span>
                ))}
              </div>
            </div>

            {/* Itineraries (both bookings under this PNR) */}
            {itineraries.map((it, idx) => <Itinerary key={idx} it={it} idx={idx} />)}

            {/* Payment */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3"><CreditCard className="w-3.5 h-3.5 text-[#328CC1]" /> Payment</p>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-[11px] text-slate-400">Total paid</p>
                  <p data-testid="mb-total" className="font-display text-2xl font-bold text-[#0B3C5D]">{gbp(booking.amount)}</p>
                  {booking.payment_method && <p data-testid="mb-payment-method" className="text-[13px] text-slate-600 mt-1">Paid via <span className="font-semibold">{booking.payment_method}</span></p>}
                </div>
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#16a34a] text-white flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Paid</span>
              </div>
            </div>

            {/* Invoice */}
            {booking.invoice_number && (
              <div data-testid="mb-invoice" className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#328CC1]" /> Invoice #{booking.invoice_number}</p>
                  <p className="text-[11px] text-slate-400">{booking.invoice_date}</p>
                </div>
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs">
                      <tr><th className="text-left font-medium px-4 py-2">Description</th><th className="text-center font-medium px-2 py-2">Qty</th><th className="text-right font-medium px-2 py-2">Unit</th><th className="text-right font-medium px-4 py-2">Amount</th></tr>
                    </thead>
                    <tbody>
                      {(booking.invoice_items || []).map((it, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-4 py-2.5 text-slate-800">{it.desc}</td>
                          <td className="px-2 py-2.5 text-center text-slate-600">{it.qty}</td>
                          <td className="px-2 py-2.5 text-right text-slate-600">{gbp(it.unit)}</td>
                          <td className="px-4 py-2.5 text-right font-medium text-slate-900">{gbp(it.amount)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-slate-200 bg-slate-50">
                        <td className="px-4 py-2.5 font-semibold text-slate-900" colSpan={3}>Total <span className="text-[#16a34a] font-semibold">· PAID (Bank transfer)</span></td>
                        <td className="px-4 py-2.5 text-right font-display font-bold text-[#0B3C5D]">{gbp(booking.invoice_total || booking.amount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Downloads */}
            <div className="flex flex-wrap gap-3">
              {booking.invoice_url && <a href={booking.invoice_url} target="_blank" rel="noopener noreferrer" data-testid="mb-download-invoice"><Button variant="outline" className="rounded-full"><Download className="w-4 h-4 mr-2 text-[#328CC1]" /> Download invoice</Button></a>}
              {booking.terms_url && <a href={booking.terms_url} target="_blank" rel="noopener noreferrer" data-testid="mb-download-terms"><Button variant="outline" className="rounded-full"><FileText className="w-4 h-4 mr-2 text-slate-500" /> Terms & Conditions (PDF)</Button></a>}
            </div>

            {/* Terms & Conditions */}
            {terms.length > 0 && (
              <div data-testid="mb-terms" className="bg-white rounded-2xl border border-slate-200 p-5">
                <p className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-[#328CC1]" /> Flight Booking Terms & Conditions</p>
                <div className="divide-y divide-slate-100">
                  {terms.map((t, i) => (
                    <details key={i} className="group py-2">
                      <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-medium text-slate-800 hover:text-[#FF6B35]">
                        {t.heading}
                        <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                      </summary>
                      <ul className="mt-2 space-y-1.5 pl-1">
                        {t.points.map((p, j) => <li key={j} className="text-[13px] text-slate-600 flex gap-2"><span className="text-[#FF6B35] mt-0.5">•</span>{p}</li>)}
                      </ul>
                    </details>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400 text-center">Need changes? Call 020 4615 3141 or email info@eliatravelandtours.co.uk quoting your reference {booking.reference}.</p>
          </div>
        )}
      </div>
    </div>
  );
}
